import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// ============================================================
// Types
// ============================================================

export interface PinStatus {
  hasPin: boolean;
}

export interface KnownDevice {
  id: string;
  userId: string;
  tenantId: string;
  deviceFingerprint: string;
  deviceName: string | null;
  ipAddress: string;
  firstSeenAt: string;
  lastSeenAt: string;
  confirmedAt: string | null;
  trusted: boolean;
  createdAt: string;
}

export interface LoginActivity {
  id: string;
  userId: string;
  status: 'SUCCESS' | 'FAILED' | 'BLOCKED';
  deviceName?: string | null;
  ipAddress: string;
  userAgent?: string | null;
  location?: string | null;
  failReason?: string | null;
  createdAt: string;
}

export interface TransactionAuditLog {
  id: string;
  tenantId: string;
  userId: string;
  virtualAccountId: string | null;
  transactionId: string | null;
  correlationId: string | null;
  operationType: string;
  amount: string | number;
  status: 'INITIATED' | 'VALIDATED' | 'EXECUTED' | 'FAILED' | 'BLOCKED';
  destinationKey: string | null;
  ipAddress: string;
  userAgent: string | null;
  deviceFingerprint: string | null;
  pinValidated: boolean;
  deviceKnown: boolean;
  rateLimitPassed: boolean;
  blockReason: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface CreatePinPayload {
  pin: string;
}

export interface UpdatePinPayload {
  currentPin: string;
  newPin: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

// ============================================================
// Query keys
// ============================================================

export const userSecurityKeys = {
  all: ['user-security'] as const,
  pinStatus: () => [...userSecurityKeys.all, 'pin-status'] as const,
  devices: () => [...userSecurityKeys.all, 'devices'] as const,
  loginActivities: (page: number, limit: number) =>
    [...userSecurityKeys.all, 'login-activities', page, limit] as const,
  transactionLogs: (page: number, limit: number) =>
    [...userSecurityKeys.all, 'transaction-logs', page, limit] as const,
};

// ============================================================
// PIN hooks
// ============================================================

export function usePinStatus() {
  return useQuery({
    queryKey: userSecurityKeys.pinStatus(),
    queryFn: async () => {
      const { data } = await api.get<PinStatus>('/banking/security/pin/status');
      return data;
    },
  });
}

export function useCreatePin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePinPayload) => {
      const { data } = await api.post<{ success: true }>(
        '/banking/security/pin',
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userSecurityKeys.pinStatus() });
    },
  });
}

export function useUpdatePin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdatePinPayload) => {
      const { data } = await api.put<{ success: true }>(
        '/banking/security/pin',
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userSecurityKeys.pinStatus() });
    },
  });
}

// ============================================================
// Devices
// ============================================================

export function useKnownDevices() {
  return useQuery({
    queryKey: userSecurityKeys.devices(),
    queryFn: async () => {
      const { data } = await api.get<KnownDevice[]>('/banking/security/devices');
      return data;
    },
  });
}

export function useRemoveDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deviceId: string) => {
      const { data } = await api.delete<{ success: true }>(
        `/banking/security/devices/${deviceId}`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userSecurityKeys.devices() });
    },
  });
}

// ============================================================
// Login activities and transaction logs
// ============================================================

export function useLoginActivities(page = 1, limit = 10) {
  return useQuery({
    queryKey: userSecurityKeys.loginActivities(page, limit),
    queryFn: async () => {
      const { data } = await api.get<Paginated<LoginActivity>>(
        '/banking/security/login-activities',
        { params: { page, limit } },
      );
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useTransactionLogs(page = 1, limit = 10) {
  return useQuery({
    queryKey: userSecurityKeys.transactionLogs(page, limit),
    queryFn: async () => {
      const { data } = await api.get<Paginated<TransactionAuditLog>>(
        '/banking/security/transaction-logs',
        { params: { page, limit } },
      );
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

// ============================================================
// Change password (used by settings and forced reset page)
// ============================================================

export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: ChangePasswordPayload) => {
      const { data } = await api.post<{ message: string }>(
        '/auth/change-password',
        payload,
      );
      return data;
    },
  });
}
